/*
 * THE PLATFORM ICONS on the Players panel. Asked for on 2026-10-07: "use
 * icons for player platforms in the list. chrome, windows, macos, all have
 * icons for example. hovering or long pressing should show more details".
 *
 * The server says what somebody plays on as one phrase, "Firefox on
 * Windows", either half alone when the other is not known (see
 * platform.go). Here it is split back into its halves and each half is a
 * small mark of its own: the browser's colours and the system's shape,
 * drawn here as simple SVG rather than fetched, so the panel never waits
 * on anything and nothing of anybody's is copied. A name with no mark is
 * left as its word.
 */

/** The phrase split into what the browser is and what it runs on. */
export function platformParts(platform: string): { browser: string; system: string } {
  const at = platform.indexOf(" on ");
  if (at >= 0) return { browser: platform.slice(0, at), system: platform.slice(at + 4) };
  // One half alone: a system name is one the server's list of systems has.
  return SYSTEMS.has(platform) ? { browser: "", system: platform } : { browser: platform, system: "" };
}

const SYSTEMS = new Set(["iPhone", "iPad", "iPod", "Android", "ChromeOS", "Windows", "macOS", "Linux"]);

/** Each mark on a 16 by 16 board. */
const MARKS: Record<string, string> = {
  // The browsers, by their colours.
  Chrome:
    '<circle cx="8" cy="8" r="7.5" fill="#db4437"/><path d="M8 8 L1.5 11.75 A7.5 7.5 0 0 1 1.5 4.25 Z" fill="#0f9d58"/><path d="M8 8 L14.5 11.75 A7.5 7.5 0 0 1 1.5 11.75 Z" fill="#f4b400"/><circle cx="8" cy="8" r="3.6" fill="#fff"/><circle cx="8" cy="8" r="2.7" fill="#4285f4"/>',
  Chromium:
    '<circle cx="8" cy="8" r="7.5" fill="#5f8fd6"/><path d="M8 8 L1.5 11.75 A7.5 7.5 0 0 1 1.5 4.25 Z" fill="#a8c4ee"/><path d="M8 8 L14.5 11.75 A7.5 7.5 0 0 1 1.5 11.75 Z" fill="#7aa6e2"/><circle cx="8" cy="8" r="3.6" fill="#fff"/><circle cx="8" cy="8" r="2.7" fill="#2f63b8"/>',
  Firefox:
    '<circle cx="8" cy="8" r="7.5" fill="#6e3bd1"/><path d="M8 0.5 A7.5 7.5 0 1 1 1.2 11 C3 13 7 13.5 9.5 11 C12 8.5 11 4.5 8 3.8 C10 2.6 9.5 1 8 0.5 Z" fill="#ff7a1a"/><circle cx="8.6" cy="7.6" r="2.6" fill="#ffd34d"/>',
  Safari:
    '<circle cx="8" cy="8" r="7.5" fill="#1c8ef9"/><circle cx="8" cy="8" r="6" fill="#eaf4ff"/><path d="M8 8 L12 4 L9 9 Z" fill="#e8413c"/><path d="M8 8 L4 12 L7 7 Z" fill="#8a96a3"/>',
  Edge:
    '<circle cx="8" cy="8" r="7.5" fill="#0c59a4"/><path d="M1 9 C1 4 4.5 1 8.5 1 C12.5 1 15 4 15 7 C15 9 13.5 10 11.5 10 L5.5 10 C5.5 12.5 8 14 11 13.5 C9 15.2 5.2 15.3 3 13 C1.7 11.7 1 10.3 1 9 Z" fill="#33c481"/>',
  Opera: '<circle cx="8" cy="8" r="7" fill="none" stroke="#ff1b2d" stroke-width="2.6"/><ellipse cx="8" cy="8" rx="2.4" ry="5.2" fill="none" stroke="#c4000f" stroke-width="1.4"/>',
  "Samsung Internet":
    '<circle cx="8" cy="8" r="7.5" fill="#6f5af0"/><circle cx="8" cy="8" r="3.4" fill="#fff"/><ellipse cx="8" cy="8" rx="6.6" ry="2" fill="none" stroke="#fff" stroke-width="1.2" transform="rotate(-25 8 8)"/>',
  // The systems, by their shapes.
  Windows:
    '<rect x="1" y="1" width="6.5" height="6.5" fill="#00a4ef"/><rect x="8.5" y="1" width="6.5" height="6.5" fill="#00a4ef"/><rect x="1" y="8.5" width="6.5" height="6.5" fill="#00a4ef"/><rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#00a4ef"/>',
  macOS:
    '<path d="M8 5 C9.5 3.6 13 3.8 13.5 7 C11.8 7.8 11.8 10.4 13.7 11.2 C13 13 11.8 15 10.3 15 C9.3 15 9 14.4 8 14.4 C7 14.4 6.6 15 5.6 15 C3.8 15 2 11.6 2 9 C2 5.4 5.4 4 8 5 Z" fill="#c9ccd1"/><path d="M8.2 4.2 C8.1 2.6 9.3 1.2 10.8 1 C10.9 2.6 9.7 4 8.2 4.2 Z" fill="#c9ccd1"/>',
  iPhone: '<rect x="4" y="0.8" width="8" height="14.4" rx="1.8" fill="#c9ccd1"/><rect x="5" y="2.4" width="6" height="10.4" fill="#2a2d33"/><circle cx="8" cy="13.9" r="0.6" fill="#2a2d33"/>',
  iPod: '<rect x="4" y="0.8" width="8" height="14.4" rx="1.8" fill="#c9ccd1"/><rect x="5" y="2.4" width="6" height="10.4" fill="#2a2d33"/><circle cx="8" cy="13.9" r="0.6" fill="#2a2d33"/>',
  iPad: '<rect x="2" y="1" width="12" height="14" rx="1.6" fill="#c9ccd1"/><rect x="3.2" y="2.4" width="9.6" height="11" fill="#2a2d33"/>',
  Android:
    '<path d="M2.5 10 A5.5 5.5 0 0 1 13.5 10 Z" fill="#3ddc84"/><rect x="2.5" y="10.6" width="11" height="4.4" rx="1" fill="#3ddc84"/><line x1="4.2" y1="3.4" x2="5.4" y2="5.4" stroke="#3ddc84" stroke-width="1"/><line x1="11.8" y1="3.4" x2="10.6" y2="5.4" stroke="#3ddc84" stroke-width="1"/><circle cx="5.8" cy="8" r="0.8" fill="#16241b"/><circle cx="10.2" cy="8" r="0.8" fill="#16241b"/>',
  ChromeOS:
    '<rect x="2.5" y="2.5" width="11" height="8" rx="0.8" fill="#c9ccd1"/><rect x="3.5" y="3.5" width="9" height="6" fill="#4285f4"/><path d="M0.5 12 L15.5 12 L14.5 14 L1.5 14 Z" fill="#c9ccd1"/>',
  Linux:
    '<ellipse cx="8" cy="9.5" rx="4.8" ry="5.8" fill="#1d1d1d"/><ellipse cx="8" cy="10.5" rx="3.2" ry="4.2" fill="#f2f2f2"/><circle cx="6.6" cy="5.4" r="0.9" fill="#fff"/><circle cx="9.4" cy="5.4" r="0.9" fill="#fff"/><path d="M6.6 6.6 L9.4 6.6 L8 8 Z" fill="#f2b41b"/><ellipse cx="5" cy="15" rx="2" ry="0.9" fill="#f2b41b"/><ellipse cx="11" cy="15" rx="2" ry="0.9" fill="#f2b41b"/>',
};

const srcs = new Map<string, string>();

/** The picture for a browser's or a system's name, or "" for one with no mark. */
export function platformIconSrc(name: string): string {
  const mark = MARKS[name];
  if (!mark) return "";
  let src = srcs.get(name);
  if (!src) {
    src = "data:image/svg+xml," + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">${mark}</svg>`);
    srcs.set(name, src);
  }
  return src;
}

/** What a hover or a long press on the marks says. */
export function platformDetail(name: string, platform: string): string {
  const { browser, system } = platformParts(platform);
  const parts = [browser && `browser: ${browser}`, system && `system: ${system}`].filter(Boolean).join(", ");
  return `${name} is playing on ${platform} (${parts}), as their browser says of itself.`;
}
